import { apiClient } from "./client";

export type Role = "manager" | "admin" | "super_admin";

export interface TenantInfo {
  id: string;
  name: string;
}

export interface MeResponse {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  language: "uk" | "ru";
  tenants: TenantInfo[];
  active_tenant_id: string | null;
  is_2fa_enabled: boolean;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
}

export interface OtpRequiredResponse {
  otp_required: true;
  otp_session_id: string;
  user_id: string;
}

export type LoginResponse = TokenPair | OtpRequiredResponse;

export function isOtpRequired(r: LoginResponse): r is OtpRequiredResponse {
  return (r as OtpRequiredResponse).otp_required === true;
}

export interface TwoFAStatus {
  is_enabled: boolean;
  is_required: boolean;
  is_telegram_linked: boolean;
  tg_username: string | null;
}

export interface TgStartLinkResponse {
  code: string;
  expires_in_sec: number;
  bot_username: string;
  deeplink: string;
}

export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>("/auth/login", { email, password }).then((r) => r.data),

  verifyOtp: (userId: string, otpSessionId: string, code: string) =>
    apiClient
      .post<TokenPair>("/auth/verify-otp", {
        user_id: userId,
        otp_session_id: otpSessionId,
        code,
      })
      .then((r) => r.data),

  me: () => apiClient.get<MeResponse>("/auth/me").then((r) => r.data),

  logout: () => apiClient.post("/auth/logout").then((r) => r.data),

  logoutAll: () => apiClient.post("/auth/logout-all").then((r) => r.data),

  switchTenant: (tenantId: string) =>
    apiClient
      .post<{ access_token: string }>("/auth/switch-tenant", { tenant_id: tenantId })
      .then((r) => r.data),

  changePassword: (oldPassword: string, newPassword: string) =>
    apiClient
      .post<TokenPair>("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      })
      .then((r) => r.data),

  // ---- 2FA ----
  get2faStatus: () => apiClient.get<TwoFAStatus>("/auth/2fa").then((r) => r.data),
  enable2fa: () => apiClient.post<TwoFAStatus>("/auth/2fa/enable").then((r) => r.data),
  disable2fa: () => apiClient.post<TwoFAStatus>("/auth/2fa/disable").then((r) => r.data),
  reset2fa: (userId: string) =>
    apiClient.post(`/auth/2fa/reset/${userId}`).then((r) => r.data),

  // ---- Telegram link ----
  startTgLink: () => apiClient.post<TgStartLinkResponse>("/tg/start-link").then((r) => r.data),
  deleteTgLink: () => apiClient.delete("/tg/link").then((r) => r.data),
};
